// Shared helpers for rendering merchant-authored anchor labels in the scene.
//
// Two patterns:
//   • buildBoxFrontLabel   — vertical placard glued to a box's front face
//                            (used by longbox / shortbox). Defined in
//                            longbox-anchor.js for the moment.
//   • buildTableSurfaceLabel — flat museum-style placard sitting on a table
//                              top, customer-side of the display. Used by
//                              pile-table / fan-table / stack-table.

import * as THREE from "../../../vendor/three.module.js";

const FLAT_LABEL_TEX_CACHE = new Map();
const SPINNER_HEADER_TEX_CACHE = new Map();
const SHELF_EDGE_TEX_CACHE = new Map();

// Render a horizontal "shelf-edge" label strip mounted on the front face
// of a shelf board. Adapts to variable shelf length via anchor.scale.x.
// One label per row maps cleanly onto our one-anchor-per-row layout.
//
// Params:
//   text         — label string (no-op if empty/null)
//   anchorPos    — shelf surface front-edge centre (THREE.Vector3, world)
//   faceDir      — outward direction (toward customer; perpendicular to wall)
//   shelfLength  — strip width in metres (use anchor.scale.x)
//   stage        — runtime Stage (for scene.add + shopLights tracking)
//   stripHeight  — vertical thickness of the strip in metres. Default 0.030.
//   yOffset      — vertical offset from anchor.y (centre of the strip).
//                  Default -0.015 puts the strip just below the comic-rest
//                  line, on the board's front lip.
//   forwardOffset— small distance along faceDir to avoid z-fight with the
//                  board mesh. Default 0.005.
export function buildShelfEdgeLabel({
	text,
	anchorPos,
	faceDir,
	shelfLength,
	stage,
	stripHeight   = 0.030,
	yOffset       = -0.015,
	forwardOffset = 0.005,
	widthOverride = null,    // when set, strip width is fixed at this metre
	                          // value regardless of shelfLength (e.g. book-row
	                          // uses a constant 10cm tag instead of a strip).
}) {
	if (!text || !stage?.scene) return null;
	const W = widthOverride != null
		? Math.max(0.05, widthOverride)
		: Math.max(0.10, shelfLength);
	const H = stripHeight;

	const aspect = W / H;
	const cacheKey = `${text}|${aspect.toFixed(2)}`;
	let tex = SHELF_EDGE_TEX_CACHE.get(cacheKey);
	if (!tex) {
		// Canvas aspect matches the plane so the text isn't stretched.
		const ch = 96;
		const cw = Math.max(256, Math.round(ch * aspect));
		const canvas = document.createElement("canvas");
		canvas.width = cw;
		canvas.height = ch;
		const ctx = canvas.getContext("2d");
		ctx.fillStyle = "#f1e7c8";
		ctx.fillRect(0, 0, cw, ch);
		ctx.strokeStyle = "#85714c";
		ctx.lineWidth = 4;
		ctx.strokeRect(2, 2, cw - 4, ch - 4);
		ctx.fillStyle = "#2c2418";
		ctx.font = `bold ${Math.round(ch * 0.62)}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(String(text).toUpperCase(), cw / 2, ch / 2 + 2);
		tex = new THREE.CanvasTexture(canvas);
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.anisotropy = 4;
		tex.needsUpdate = true;
		SHELF_EDGE_TEX_CACHE.set(cacheKey, tex);
	}

	const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95, metalness: 0 });
	const mesh = new THREE.Mesh(new THREE.PlaneGeometry(W, H), mat);

	// Position: anchor + faceDir * forwardOffset + (0, yOffset, 0)
	mesh.position.copy(anchorPos).addScaledVector(faceDir, forwardOffset);
	mesh.position.y += yOffset;

	// Orientation: plane normal (local +Z) → faceDir; world up (local +Y) → world +Y.
	const upWorld = new THREE.Vector3(0, 1, 0);
	const zAxis = faceDir.clone().normalize();
	const xAxis = new THREE.Vector3().crossVectors(upWorld, zAxis).normalize();
	const yAxis = upWorld;
	const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
	mesh.quaternion.setFromRotationMatrix(basis);

	stage.scene.add(mesh);
	stage.shopLights.push(mesh);
	return mesh;
}

// Fixture-kind → short default label. Used when neither a Storefront
// override nor a GLB extras value is present, so every visible fixture
// gets *some* label rather than being unlabeled. Merchants can clear or
// rename via the Studio per-anchor panel.
export const DEFAULT_FIXTURE_LABELS = {
	"longbox":          "Long Box",
	"shortbox":         "Short Box",
	"wall-shelf":       "Shelf",
	"book-row":         "Books",
	"pile-table":       "Pile",
	"fan-table":        "Fan",
	"stack-table":      "Stack",
	"spinner-rack":     "Spinner",
	"featured-comic":   "Featured",
	"vinyl-wall-shelf": "Vinyl Shelf",
	"vinyl-bin":        "Vinyl Bin",
};

// Three-tier label resolution: Storefront knob → GLB extras → kind default.
// Returns null only if the fixture kind is unknown.
export function resolveAnchorLabel(scene, anchor, fixtureKind) {
	const override = scene?.template?.knobs?.boxOrientations?.[anchor?.name]?.label;
	const extras   = anchor?.extras?.label;
	return override ?? extras ?? DEFAULT_FIXTURE_LABELS[fixtureKind] ?? null;
}

// Render the merchant-authored label as a canvas-based texture suitable
// for the four `header_logo_face_*` meshes on the spinner-rack GLB. Bold
// cream-on-dark "comic shop sign" aesthetic so the rack reads as a
// labelled fixture from any angle.
export function buildSpinnerHeaderTexture(text) {
	if (!text) return null;
	const key = String(text);
	let tex = SPINNER_HEADER_TEX_CACHE.get(key);
	if (tex) return tex;
	const cw = 512;
	const ch = 128;
	const canvas = document.createElement("canvas");
	canvas.width = cw;
	canvas.height = ch;
	const ctx = canvas.getContext("2d");
	// Horizontal X-flip — REQUIRED, coupled to the header mesh's face winding (coord a229267,
	// orbit-validated 2026-07-07). With Content's OUTWARD winding live, the header UVs are
	// X-mirrored relative to canvas drawing, so this flip makes the text read right-way-round;
	// outward winding + this flip = correct text + opaque faces. (Do NOT remove: it was briefly
	// pulled 2026-07-06 when the header was occluded/un-validated; flipping the winding
	// re-inverted the text.) flipY=false below stays — the glTF UV convention.
	ctx.translate(cw, 0);
	ctx.scale(-1, 1);
	ctx.fillStyle = "#1a1410";
	ctx.fillRect(0, 0, cw, ch);
	ctx.strokeStyle = "#f1e7c8";
	ctx.lineWidth = 6;
	ctx.strokeRect(4, 4, cw - 8, ch - 8);
	ctx.fillStyle = "#f1e7c8";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	// ASK-129 — FIT THE TEXT TO THE BOARD. The size was fixed, so anything longer than a short header
	// simply overflowed the canvas and was clipped by the mesh: photographed on the hub, a shop called
	// `spider-web-comics-test101b-2` read "EB-COMICS-TE" with both ends cut off. Shrink first, because
	// a smaller name is still the name; truncate only when shrinking would make it unreadable, and say
	// so with an ellipsis rather than ending mid-word as if the sign were broken.
	const inner = cw - 44;                       // inside the drawn border, both sides
	let size = Math.round(ch * 0.55), drawn = String(text).toUpperCase();
	const F = (n) => `bold ${n}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
	ctx.font = F(size);
	while (size > 26 && ctx.measureText(drawn).width > inner) ctx.font = F(size -= 2);
	while (drawn.length > 4 && ctx.measureText(drawn).width > inner) drawn = drawn.slice(0, -2) + "…";
	ctx.fillText(drawn, cw / 2, ch / 2 + 2);
	tex = new THREE.CanvasTexture(canvas);
	tex.colorSpace = THREE.SRGBColorSpace;
	tex.anisotropy = 4;
	// Spinner header is glTF-imported geometry whose UV layout assumes
	// flipY=false (the glTF convention). Three's CanvasTexture default of
	// flipY=true would render the text upside down.
	tex.flipY = false;
	tex.needsUpdate = true;
	SPINNER_HEADER_TEX_CACHE.set(key, tex);
	return tex;
}


// Render a small cardboard-style label flat on a table surface, positioned
// in front of the display from the customer's POV (so the customer reads
// the label before glancing at the comics). The plane lies horizontally
// just above the table top to avoid z-fighting with the slab.
//
// Params:
//   text         — label string (no-op if empty/null)
//   anchorPos    — fixture anchor position (THREE.Vector3, world)
//   frontDir     — direction the fixture's front faces (unit XZ vector)
//   stage        — runtime Stage (for scene.add + shopLights tracking)
//   offset       — distance along frontDir from anchor to label centre.
//                  Defaults to 0.18m — clears the typical fan/pile/stack
//                  comic footprint (±0.13m).
//   widthM       — label width in metres. Defaults to 0.10m.
//   heightM      — label depth (text-line direction) in metres. Defaults
//                  to 0.025m.
//   yLift        — small Y offset above the table top to avoid z-fight.
//                  Defaults to 0.002m.
export function buildTableSurfaceLabel({
	text,
	anchorPos,
	frontDir,
	stage,
	offset  = 0.18,
	widthM  = 0.10,
	heightM = 0.025,
	yLift   = 0.002,
}) {
	if (!text || !stage?.scene) return null;
	const key = String(text);
	let tex = FLAT_LABEL_TEX_CACHE.get(key);
	if (!tex) {
		const cw = 512;
		const ch = Math.round(cw * heightM / widthM);
		const canvas = document.createElement("canvas");
		canvas.width = cw; canvas.height = ch;
		const ctx = canvas.getContext("2d");
		ctx.fillStyle = "#f1e7c8";
		ctx.fillRect(0, 0, cw, ch);
		ctx.strokeStyle = "#85714c";
		ctx.lineWidth = 4;
		ctx.strokeRect(2, 2, cw - 4, ch - 4);
		ctx.fillStyle = "#2c2418";
		ctx.font = `bold ${Math.round(ch * 0.60)}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
		ctx.textAlign = "center";
		ctx.textBaseline = "middle";
		ctx.fillText(String(text).toUpperCase(), cw / 2, ch / 2 + 1);
		tex = new THREE.CanvasTexture(canvas);
		tex.colorSpace = THREE.SRGBColorSpace;
		tex.anisotropy = 4;
		tex.needsUpdate = true;
		FLAT_LABEL_TEX_CACHE.set(key, tex);
	}

	const mat = new THREE.MeshStandardMaterial({ map: tex, roughness: 0.95, metalness: 0 });
	const mesh = new THREE.Mesh(new THREE.PlaneGeometry(widthM, heightM), mat);

	// Position: anchor + frontDir * offset, lifted slightly above the table.
	mesh.position.copy(anchorPos).addScaledVector(frontDir, offset);
	mesh.position.y += yLift;

	// Orientation: plane lies flat (normal = +Y world). Plane's local +Y
	// (top-of-text) points OPPOSITE the customer (i.e. -frontDir) so the
	// text reads right-side up when the customer looks down at it from
	// the +frontDir side. Use makeBasis to build the rotation directly.
	const upWorld = new THREE.Vector3(0, 1, 0);
	const zAxis = upWorld;                                                // plane normal = +Y
	const yAxis = frontDir.clone().negate();                              // text-top → -frontDir
	const xAxis = new THREE.Vector3().crossVectors(yAxis, zAxis).normalize();
	const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
	mesh.quaternion.setFromRotationMatrix(basis);

	stage.scene.add(mesh);
	stage.shopLights.push(mesh);
	return mesh;
}
